import React, { useState } from 'react';
import peterAvatar from '../../assets/illustrations/medical-savings-peter.webp';
import reservePiggy from '../../assets/illustrations/medical-reserve-piggy.webp';
import './MedicalSavings.scss';

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };
const STAGES = ['pay', 'grow', 'use'] as const;
// Original rounded milestones: an educational illustration, not a calculator.
const MILESTONES = [
  { age: 40, value: 47 },
  { age: 65, value: 163 },
  { age: 85, value: 91 }
];
const x = (age: number) => 52 + ((age - 30) / 55) * 650;
const y = (value: number) => 340 - value * 1.35;
const GROWTH = `M ${x(30)} 340 Q ${x(35)} ${y(20)} ${x(40)} ${y(47)} Q ${x(55)} ${y(90)} ${x(65)} ${y(163)}`;

const MedicalSavings = ({ intl, onOpenCalculator }: { intl: Intl; onOpenCalculator?: () => void }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id: `msv-${id}` }, values);
  const amount = (value: number) =>
    t('amount', { n: intl.locale === 'en' ? (value * 10000).toLocaleString('en') : value });
  const [stage, setStage] = useState<(typeof STAGES)[number]>('pay');
  return (
    <article className="medical-savings" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="msv-head">
        <h2>
          <span>{t('title')}</span>
        </h2>
        <p>{t('intro')}</p>
      </header>
      <section className="msv-profile" aria-label={t('example')}>
        <img className="msv-peter" src={peterAvatar} width="80" height="80" alt="" />
        <div>
          <h3>{t('person')}</h3>
          <p>{t('policy')}</p>
          <p>{t('premium')}</p>
        </div>
      </section>
      <p className="msv-plan">{t('plan')}</p>
      <figure className={`msv-chart is-${stage}`}>
        <figcaption>{t('chart-title')}</figcaption>
        <p className="msv-current-stage">
          {STAGES.indexOf(stage) + 1} · {t(`stage-${stage}`)}
        </p>
        <p className="msv-chart-hint">{t('chart-hint')}</p>
        <div className="msv-chart-canvas" tabIndex={0} role="region" aria-label={t('chart-label')}>
          <svg viewBox="0 0 760 455" role="img" aria-label={t('chart-label')}>
            {[0, 50, 100, 150, 200].map(value => (
              <g key={value}>
                <line x1="52" x2="702" y1={y(value)} y2={y(value)} className="msv-grid" />
                <text x="42" y={y(value) + 5} textAnchor="end">
                  {value}
                </text>
              </g>
            ))}
            <text x="52" y="34">
              {t('unit')}
            </text>
            <path d={`${GROWTH} L ${x(65)} 340 Z`} className="msv-growth" />
            <path
              d={`M ${x(65)} 340 L ${x(65)} ${y(163)} L ${x(85)} ${y(91)} L ${x(85)} 340 Z`}
              className="msv-reserve"
            />
            <path
              d={`M ${x(40)} 340 L ${x(40)} ${y(36)} L ${x(65)} ${y(36)} L ${x(65)} 340 Z`}
              className="msv-principal"
            />
            <path d={`M ${x(30)} 340 L ${x(40)} ${y(36)} L ${x(40)} 340 Z`} className="msv-contribution" />
            <line x1={x(40)} x2={x(65)} y1={y(36)} y2={y(36)} className="msv-marker" />
            <path d={`${GROWTH} L ${x(85)} ${y(91)}`} className="msv-curve" />
            {[40, 65].map(age => (
              <line
                key={age}
                x1={x(age)}
                x2={x(age)}
                y1={age === 40 ? y(47) : y(163)}
                y2="340"
                className="msv-marker"
              />
            ))}
            {[
              { age: 40, value: 47, size: 64 },
              { age: 50, value: 77, size: 72 },
              { age: 57, value: 115, size: 84 },
              { age: 65, value: 163, size: 106 },
              { age: 85, value: 91, size: 80 }
            ].map(({ age, value, size }) => (
              <image
                key={age}
                href={reservePiggy}
                x={x(age) - size / 2}
                y={y(value) - size + 5}
                width={size}
                height={size}
                aria-hidden="true"
              />
            ))}
            {MILESTONES.map(({ age, value }) => (
              <text
                key={age}
                x={x(age)}
                y={y(value) - (age === 65 ? 102 : age === 40 ? 62 : 78)}
                textAnchor="middle"
                className="msv-amount"
              >
                {amount(value)}
              </text>
            ))}
            <g className="msv-deposits">
              <text x="80" y="213" className="msv-deposit-label">
                {t('total-paid')}
              </text>
              <text x="80" y="238" className="msv-deposit-label">
                {amount(36)}
              </text>
              <path
                d={`M ${x(31)} 333 Q ${x(35)} 311 ${x(39)} 287 l -10 2 m 10 -2 l -4 9`}
                className="msv-deposit-flow"
              />
              {[32, 34, 36, 38].map((age, i) => (
                <g key={age} className="msv-deposit-coin" aria-hidden="true">
                  <circle cx={x(age)} cy={310 - i * 10} r="11" />
                  <text x={x(age)} y={315 - i * 10} textAnchor="middle">
                    $
                  </text>
                </g>
              ))}
            </g>
            {[67, 71, 75, 79, 83].map(age => (
              <g key={age} className="msv-payment" aria-hidden="true">
                <path d={`M ${x(age)} ${y(163 - (age - 65) * 3.6) + 5} q -12 -28 8 -40 l -10 1 m 10 -1 l -2 10`} />
                <circle cx={x(age)} cy={y(163 - (age - 65) * 3.6) + 21} r="10" />
                <text x={x(age)} y={y(163 - (age - 65) * 3.6) + 26} textAnchor="middle">
                  $
                </text>
              </g>
            ))}
            <g className="msv-medical-bill" transform="translate(556 10) scale(0.86) rotate(8 46 50)">
              <path d="M 0 0 H 70 L 91 21 V 104 H 0 Z" className="msv-bill-paper" />
              <path d="M 70 0 V 21 H 91" className="msv-bill-fold" />
              <path d="M 39 18 H 51 V 29 H 62 V 41 H 51 V 52 H 39 V 41 H 28 V 29 H 39 Z" className="msv-bill-cross" />
              <path d="M 15 63 H 76 M 15 71 H 76" className="msv-bill-lines" />
              <text x="45" y="94" textAnchor="middle" className="msv-bill-amount">
                {amount(72)}
              </text>
            </g>
            <g className="msv-premium-callout" transform="translate(500 265)">
              <rect width="192" height="58" rx="8" />
              <text x="96" y="23" textAnchor="middle">
                {t('premium-payment')}
              </text>
              <text x="96" y="46" textAnchor="middle" className="msv-premium-total">
                {t('premium-total', { amount: amount(72) })}
              </text>
            </g>
            <text
              x={(x(30) + 2 * x(40)) / 3}
              y={(340 + 340 + y(36)) / 3}
              dominantBaseline="central"
              textAnchor="middle"
              className="msv-area-label msv-contribution-label"
            >
              {t('contribution')}
            </text>
            <text
              x={(x(40) + x(65)) / 2}
              y={(340 + y(36)) / 2}
              dominantBaseline="central"
              textAnchor="middle"
              className="msv-area-label msv-principal-label"
            >
              {t('principal')}
            </text>
            <text
              x={x(56)}
              y="240"
              dominantBaseline="central"
              textAnchor="middle"
              className="msv-area-label msv-growth-label"
            >
              2 · {t('growth')}
            </text>
            <text
              x={(x(65) + x(85)) / 2}
              y="245"
              dominantBaseline="central"
              textAnchor="middle"
              className="msv-area-label msv-reserve-label"
            >
              3 · {t('reserve')}
            </text>
            {[30, 40, 50, 65, 75, 85].map(age => (
              <text key={age} x={x(age)} y="365" textAnchor="middle">
                {age}
              </text>
            ))}
            <text x="702" y="450" textAnchor="end">
              {t('age-axis')}
            </text>
            {[
              { name: 'pay', start: 30, end: 40 },
              { name: 'grow', start: 40, end: 65 },
              { name: 'use', start: 65, end: 85 }
            ].map(({ name, start, end }, i) => (
              <g key={name} className={`msv-timeline ${stage === name ? 'is-active' : ''}`}>
                <text x={(x(start) + x(end)) / 2} y="396" textAnchor="middle">
                  {i + 1} · {t(`period-${name}`)}
                </text>
                <path d={`M ${x(start) + 5} 414 H ${x(end) - 5} l -7 -5 m 7 5 l -7 5`} />
              </g>
            ))}
          </svg>
        </div>
      </figure>
      <div className="msv-story" role="group" aria-label={t('walkthrough')}>
        {STAGES.map((name, i) => (
          <button key={name} type="button" aria-pressed={stage === name} onClick={() => setStage(name)}>
            <span className="msv-step-number">{i + 1}</span>
            {stage === name && (
              <span className="msv-selected-mark" aria-hidden="true">
                ✓
              </span>
            )}
            <span>
              <strong>{t(`stage-${name}`)}</strong>
              <small>{t(`years-${name}`)}</small>
            </span>
          </button>
        ))}
      </div>
      <section className={`msv-explanation is-${stage}`} aria-live="polite" aria-atomic="true">
        <img src={reservePiggy} alt="" width="96" height="96" />
        <div>
          <h3>
            {STAGES.indexOf(stage) + 1} · {t(`stage-${stage}`)}
          </h3>
          <p>{t(`story-${stage}`)}</p>
          <p className="msv-story-lesson">{t(`takeaway-${stage}`)}</p>
        </div>
      </section>
      <p className="msv-conclusion">{t('lesson')}</p>
      {onOpenCalculator && (
        <div className="msv-calculator-link">
          <button type="button" onClick={onOpenCalculator}>
            {t('open-calculator')} <span aria-hidden="true">→</span>
          </button>
        </div>
      )}
      <footer className="msv-notes">
        <p>{t('source')}</p>
        <p>{t('illustration-note')}</p>
        <p>
          {t('disclaimer-1')}
          <br />
          {t('disclaimer-2')}
          <br />
          {t('disclaimer-3')}
        </p>
        <p className="msv-risk">{t('risk')}</p>
        <p>
          {t('references')}{' '}
          <a href="https://www.ia.org.hk/en/participating_policy/index.html" target="_blank" rel="noreferrer">
            {t('ref-return')}
          </a>{' '}
          ·{' '}
          <a href="https://education.ia.org.hk/en/product/medical-insurance.html" target="_blank" rel="noreferrer">
            {t('ref-premium')}
          </a>
        </p>
      </footer>
    </article>
  );
};
export default MedicalSavings;
